import React, { useState, useMemo } from "react";
import { withRouter } from "react-router-dom";
import moment from "moment";
import FilterBar from "./FilterBar";
import { Divider, Row, Col, Card } from "antd";
import { mapValues, groupBy, omit, isEmpty } from "lodash";

// Global Styling
import BasketItemsTable from "./BasketItemsTable";
import DetailsBar from "./DetailsBar";
import BasketItemsBarChart from "./BasketItemsBarChart";
import Charts from "./charts/Charts";
import { colors } from "../../../../../../../core/utils/constants/listOfColors";

const aggregateDataFunc = (isValid, sortedData) => {
  let aggregatedData;
  if (isValid && !isEmpty(sortedData)) {
    aggregatedData = mapValues(groupBy(sortedData, "ORDER_ID"), list =>
      list.map(obj => omit(obj, "ORDER_ID"))
    );
  }

  return aggregatedData;
};

const BasketItemsLayout = withRouter(
  ({
    chartData,
    location,
    permission,
    reportName,
    basketItemsTableLoading,
    basketItemsTable,
    setCurrentIdx,
    searchTerm,
    setSearchTerm,
    attributionWindow,
    setAttributionWindow,
    campaignOrderId,
  }) => {
    const hasSimplifiedReport = chartData && chartData.hasSimplifiedReport;
    const data = chartData && chartData.data;
    const s3SelectData = basketItemsTable && basketItemsTable.data;
    const s3SelectTotalCount = basketItemsTable && basketItemsTable.count;

    const [currentPage, setCurrentPage] = useState(1);

    const params = new URLSearchParams(location.search);
    const search = params.get("search") || "";
    const orderId = params.get("orderId") || "";
    const dropdownFilter = params.get("df");

    const [searchVal, setSearchVal] = useState(
      search ? search.split(",") : null
    );
    const [orderIdVal, setOrderIdVal] = useState(orderId);

    const isValid = Array.isArray(data) && data.length > 0;
    const s3DataIsValid =
      Array.isArray(s3SelectData) && s3SelectData.length > 0;

    const sortedDataByDate = isValid
      ? data.sort((a, b) => moment(a.DATE) - moment(b.DATE))
      : [];
    const sortedS3DataByDate = s3DataIsValid
      ? s3SelectData.sort((a, b) => a.DATE - b.DATE)
      : [];

    let newData = isValid ? sortedDataByDate : [];
    let newS3Data = s3DataIsValid ? sortedS3DataByDate : [];

    const filterByOrderId = useMemo(() => {
      return obj => obj.ORDER_ID.toLowerCase().includes(orderId.toLowerCase());
    }, [orderId]);

    if (isValid && orderId) {
      if (hasSimplifiedReport === false) {
        newData = chartData && chartData.data.filter(filterByOrderId);
      } else {
        newData = chartData && chartData.data.filter(filterByOrderId);
        newS3Data = s3SelectData && s3SelectData.filter(filterByOrderId);
      }
    }

    const filteredChartDataBySearchTerm = useMemo(() => {
      const searchStrings = search
        .split(",")
        .map(str => str.trim().toLowerCase());
      return obj => {
        return searchStrings.some(searchStr =>
          obj.NAME.toLowerCase().includes(searchStr)
        );
      };
    }, [search]);

    if (isValid && search) {
      if (hasSimplifiedReport === false) {
        newData =
          chartData && chartData.data.filter(filteredChartDataBySearchTerm);
      } else {
        setSearchTerm(search);
        newData =
          chartData && chartData.data.filter(filteredChartDataBySearchTerm);
        newS3Data =
          chartData && chartData.data.filter(filteredChartDataBySearchTerm);
      }
    }

    if (isValid && dropdownFilter && dropdownFilter !== "All Time") {
      let days;
      if (dropdownFilter === "Last 7 Days") days = 7;
      if (dropdownFilter === "Last 15 Days") days = 15;
      if (dropdownFilter === "Last 30 Days") days = 30;
      if (dropdownFilter === "Last 60 Days") days = 60;
      if (dropdownFilter === "Last 90 Days") days = 90;

      if (hasSimplifiedReport === false) {
        newData = newData.filter(
          obj =>
            moment(obj.DATE).format("YYYY-MM-DD") >=
              moment()
                .subtract(days, "d")
                .format("YYYY-MM-DD") &&
            moment(obj.DATE).format("YYYY-MM-DD") <=
              moment().format("YYYY-MM-DD")
        );
      } else {
        setAttributionWindow(days);
        newData = newData.filter(
          obj =>
            moment(obj.DATE).format("YYYY-MM-DD") >=
              moment()
                .subtract(days, "d")
                .format("YYYY-MM-DD") &&
            moment(obj.DATE).format("YYYY-MM-DD") <=
              moment().format("YYYY-MM-DD")
        );
        newS3Data =
          newS3Data &&
          newS3Data.filter(
            obj =>
              moment(obj.DATE).format("YYYY-MM-DD") >=
                moment()
                  .subtract(days, "d")
                  .format("YYYY-MM-DD") &&
              moment(obj.DATE).format("YYYY-MM-DD") <=
                moment().format("YYYY-MM-DD")
          );
      }
    }

    const aggregatedData = aggregateDataFunc(isValid, newData);
    const aggregatedS3Data = aggregateDataFunc(s3DataIsValid, newS3Data);

    const tableData = [];
    const exportData = [];

    let totalRevenue = 0;
    let totalQuantity = 0;
    let revenueTableAggregatedData = [];

    const basketItemsSortedByName = newData.sort((a, b) =>
      a.NAME > b.NAME ? 1 : b.NAME > a.NAME ? -1 : 0
    );

    const filteredBasketItemsSortedByName =
      !isEmpty(basketItemsSortedByName) &&
      basketItemsSortedByName.filter(
        item =>
          item.ORDER_ID.toLowerCase().includes(
            search ? search.toLowerCase().trim() : ""
          ) ||
          item.NAME.toLowerCase().includes(
            search ? search.toLowerCase().trim() : ""
          )
      );

    if (!isEmpty(filteredBasketItemsSortedByName)) {
      revenueTableAggregatedData = groupBy(
        filteredBasketItemsSortedByName,
        "NAME"
      );
    }

    Object.keys(revenueTableAggregatedData).map(key => {
      const byNameDataArray = revenueTableAggregatedData[key];
      let revenue = 0;
      let quantity = 0;
      for (let item of byNameDataArray) {
        revenue += item.PRICE * item.QUANTITY;
        quantity += item.QUANTITY;
      }

      tableData.push({
        NAME: key,
        REVENUE: revenue,
        QUANTITY: quantity,
      });
      return key;
    });

    for (let i = 0; i < newData.length; i++) {
      totalRevenue +=
        parseFloat(newData[i].PRICE) * parseInt(newData[i].QUANTITY);
      totalQuantity += parseInt(newData[i].QUANTITY);
    }

    exportData.push(...tableData, {
      NAME: "TOTAL",
      REVENUE: totalRevenue,
      QUANTITY: totalQuantity,
    });

    const colorValuePairs = tableData.map((el, index) => {
      return {
        ...el,
        color: colors[index],
      };
    });

    return (
      <Card>
        <Row>
          <Col span={24}>
            <FilterBar
              data={exportData}
              searchVal={searchVal}
              setSearchVal={setSearchVal}
              setOrderIdVal={setOrderIdVal}
              params={params}
              dropdownFilter={dropdownFilter}
              permission={permission}
              hasSimplifiedReport={hasSimplifiedReport}
              setSearchTerm={setSearchTerm}
              setAttributionWindow={setAttributionWindow}
              setCurrentPage={setCurrentPage}
            />
          </Col>
        </Row>
        <Divider />
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={{ span: 18, order: 1, push: 6 }}>
            <DetailsBar
              data={newData}
              aggregatedData={aggregatedData}
              hasSimplifiedReport={hasSimplifiedReport}
              basketItemsTable={basketItemsTable}
              basketItemsTableLoading={basketItemsTableLoading}
            />
          </Col>
          <Col id="basket-items-lg-table" xs={24} lg={{ span: 6 }}>
            <BasketItemsTable
              orderIdVal={orderIdVal}
              setSearchVal={setSearchVal}
              setOrderIdVal={setOrderIdVal}
              aggregatedData={
                hasSimplifiedReport ? aggregatedS3Data : aggregatedData
              }
              hasSimplifiedReport={hasSimplifiedReport}
              basketItemsTableLoading={basketItemsTableLoading}
              basketItemsTable={basketItemsTable}
              s3SelectTotalCount={s3SelectTotalCount}
              setCurrentIdx={setCurrentIdx}
              reportName={reportName}
              campaignOrderId={campaignOrderId}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              searchTerm={searchTerm}
            />
          </Col>
          <Col xs={24} lg={{ span: 18, push: 6, order: 2 }}>
            <BasketItemsBarChart
              data={newData}
              basketItemsTableLoading={basketItemsTableLoading}
            />
          </Col>

          <Col xs={24} lg={{ span: 18, push: 6, order: 3 }}>
            <Charts
              basketItemsTableLoading={basketItemsTableLoading}
              chartData={colorValuePairs}
              chartsLoading={false}
            />
          </Col>
        </Row>
      </Card>
    );
  }
);

export default BasketItemsLayout;
